import { useCallback, useMemo, useState, useTransition } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import type { IKeywordGroup } from "app/pages/digital-marketing/common/hooks/useKeywordGroups";
import type { LocationInfo } from "app/pages/digital-marketing/keywords-campaigns/shared/components/LocationsTooltip";
import { GSC_KEYWORDS_SOURCE_ID } from "app/pages/digital-marketing/keywords-campaigns/shared/constants";
import type {
    GscSyncInput,
    SavedGscMarket,
} from "app/pages/digital-marketing/keywords-campaigns/shared/gsc-import/types";
import type {
    IImportedKeywords,
    IKeyword,
    IManualKeywords,
} from "app/pages/digital-marketing/keywords-campaigns/shared/types";
import { normalizeKeyword } from "app/pages/digital-marketing/keywords-campaigns/shared/utils/keywordNormalization";

const INITIAL_MANUAL_SOURCE: IManualKeywords = {
    id: "manual",
    type: "manual",
    keywords: [],
};

const dedupeKeywordsByValue = (keywords: IKeyword[]): IKeyword[] => {
    const seen = new Map<string, IKeyword>();
    for (const kw of keywords) {
        const key = normalizeKeyword(kw.value);
        if (!seen.has(key)) {
            seen.set(key, { ...kw });
        }
    }
    return Array.from(seen.values());
};

const buildKeywordsFromGscMarket = (market: SavedGscMarket): IKeyword[] =>
    market.keywords.map((keyword) => ({
        sourceId: GSC_KEYWORDS_SOURCE_ID,
        value: keyword.keyword,
        tags: [],
        searchEnginesIds: [],
    }));

const buildGscImportedSource = (input: GscSyncInput, sourceName: string): IImportedKeywords => {
    const allKeywords: IKeyword[] = [];
    const locations: LocationInfo[] = [];

    for (const market of input.savedMarkets.values()) {
        allKeywords.push(...buildKeywordsFromGscMarket(market));
        locations.push({
            countryCode: market.countryCode,
            languageName: market.language.text,
            device: market.device,
            keywordsCount: market.keywords.length,
        });
    }

    const deduped = dedupeKeywordsByValue(allKeywords);

    return {
        id: GSC_KEYWORDS_SOURCE_ID,
        type: "gsc",
        name: sourceName,
        keywords: deduped,
        locations,
        importedKeywordsCount: deduped.length,
    };
};

export const useKeywordsState = (gscSourceNameKey: string) => {
    const { translate } = useTranslate();
    const [manualSource, setManualSource] = useState<IManualKeywords>(INITIAL_MANUAL_SOURCE);
    const [importSources, setImportSources] = useState<IImportedKeywords[]>([]);
    const [isGscModalOpen, setIsGscModalOpen] = useState(false);
    const [hasImportError, setHasImportError] = useState(false);
    const [isImporting, startTransition] = useTransition();

    const handleManualInput = useCallback((source: IManualKeywords) => {
        setManualSource(source);
    }, []);

    const handleCsvImport = useCallback((source: IImportedKeywords) => {
        startTransition(() => {
            setImportSources((prev) => {
                if (prev.some((s) => s.id === source.id)) {
                    return prev;
                }
                return [...prev, source];
            });
        });
    }, []);

    const handleRemoveImport = useCallback((id: string) => {
        setImportSources((prev) => prev.filter((s) => s.id !== id));
    }, []);

    const handleKeywordListImport = useCallback((groups: IKeywordGroup[]) => {
        const newSources: IImportedKeywords[] = groups.map((group) => ({
            id: group.Id,
            name: group.Name,
            type: "list",
            keywords: group.Keywords.map((kw) => ({
                sourceId: group.Id,
                value: kw,
                tags: [group.Name],
                searchEnginesIds: [],
            })),
        }));

        startTransition(() => {
            setImportSources((prev) => {
                const existingIds = new Set(prev.map((s) => s.id));
                const filtered = newSources.filter((s) => !existingIds.has(s.id));
                return [...prev, ...filtered];
            });
        });
    }, []);

    const handleGscImport = useCallback(
        (input: GscSyncInput) => {
            const gscSource = buildGscImportedSource(input, translate(gscSourceNameKey));

            startTransition(() => {
                setImportSources((prev) => {
                    const withoutGsc = prev.filter((s) => s.type !== "gsc");
                    return gscSource.keywords.length > 0 ? [...withoutGsc, gscSource] : withoutGsc;
                });
            });
            setIsGscModalOpen(false);
        },
        [gscSourceNameKey, translate],
    );

    const totalKeywordsCount = useMemo(
        () => manualSource.keywords.length + importSources.reduce((sum, s) => sum + s.keywords.length, 0),
        [manualSource.keywords.length, importSources],
    );

    const allKeywords = useMemo(() => {
        const all = [...manualSource.keywords, ...importSources.flatMap((s) => s.keywords)];
        return dedupeKeywordsByValue(all);
    }, [manualSource.keywords, importSources]);

    const addedKeywordListIds = useMemo(
        () => new Set(importSources.filter((s) => s.type === "list").map((s) => s.id)),
        [importSources],
    );

    return {
        manualSource,
        importSources,
        handleManualInput,
        handleCsvImport,
        handleRemoveImport,
        handleKeywordListImport,
        handleGscImport,
        isGscModalOpen,
        setIsGscModalOpen,
        isImporting,
        hasImportError,
        setHasImportError,
        totalKeywordsCount,
        allKeywords,
        addedKeywordListIds,
    };
};
